import React, { useMemo, useState } from "react";
import { useMediaQuery } from 'react-responsive';
import './../../../style/Vars.css';
import './../../../style/ElementsStyle.css';
import './../../../style/ContainersStyles.css';

const pad = (n) => String(n).padStart(2, "0");
const daysES = ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"];

const eventGradientMap = {
  pending: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
  completed: "linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)",
  default: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
};

function getEventGradient(meta = {}) {
  const estadoKey = (meta.estadoKey || meta.idEstadoReserva || "").toString().toUpperCase();
  const tripStatus = (meta.viajeStatus || "").toString().toUpperCase();

  if (tripStatus === "COMPLETADO") return eventGradientMap.completed;
  if (estadoKey === "PENDING" || estadoKey === "1" || estadoKey === "PENDIENTE") {
    return eventGradientMap.pending;
  }
  return eventGradientMap.default;
}

function mondayStart(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function hm(date) {
  return date.getHours() * 60 + date.getMinutes();
}

function getInitials(name) {
  if (!name) return '';
  return name
    .split(' ')
    .map(part => part[0])
    .filter(Boolean)
    .join('')
    .toUpperCase();
}

const MobileOnly = ({ children }) => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  return isMobile ? children : null;
};

export default function WeeklyCalendar({
  anchorDate,
  onNavigate,
  events = [],
  availabilityBlocks = [],
  onEventClick,
  showSchedule = false // New prop to toggle view mode
}) {
  const monday = useMemo(() => mondayStart(anchorDate), [anchorDate]);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(monday, i)),
    [monday]
  );
  const hours = useMemo(
    () => Array.from({ length: 24 }, (_, h) => `${pad(h)}:00`),
    []
  );

  const weekLabel = useMemo(() => {
    const end = addDays(monday, 6);

    // Validar que las fechas sean válidas antes de formatear
    if (!monday || isNaN(monday.getTime()) || !end || isNaN(end.getTime())) {
      return "Fecha inválida";
    }

    const fmt = new Intl.DateTimeFormat("es-MX", {
      month: "long",
      year: "numeric",
    });

    try {
      return fmt.formatRange
        ? fmt.formatRange(monday, end)
        : `${monday.toLocaleString("es-MX", {
          month: "long",
        })} – ${end.toLocaleString("es-MX", {
          month: "long",
        })} ${end.getFullYear()}`;
    } catch (error) {
      console.error("Error formateando rango de fechas:", error);
      return "Fecha inválida";
    }
  }, [monday]);

  function goto(deltaDays) {
    onNavigate(addDays(monday, deltaDays));
  }

  function blockStyle(start, end, isNextDayPart = false) {
    const startM = hm(start);
    let endM = hm(end);
    const HOUR_HEIGHT = 38;

    if (endM === 0 && end.getTime() > start.getTime()) {
      const endDay = new Date(end);
      endDay.setHours(0, 0, 0, 0);
      const startDay = new Date(start);
      startDay.setHours(0, 0, 0, 0);

      if (endDay.getTime() > startDay.getTime()) {
        endM = 1440;
      }
    }

    let duration = endM - startM;
    if (duration < 0) duration += 1440;

    const top = (startM / 60) * HOUR_HEIGHT;
    const h = (duration / 60) * HOUR_HEIGHT;

    return { top: `${top}px`, height: `${Math.max(h, HOUR_HEIGHT / 2)}px` };
  }

  function handleEventClick(event) {
    if (onEventClick) {
      onEventClick(event);
    }
  }

  function DayColumn({ dayDate }) {
    const dayAvail = availabilityBlocks.filter((a) =>
      sameDay(a.start, dayDate)
    );

    return (
      <div className="wc-col">
        <div className="wc-col-body">
          {dayAvail.map((a, idx) => (
            <div
              key={`av-${idx}`}
              className="wc-block wc-availability"
              style={{
                ...blockStyle(a.start, a.end),
                background: "var(--color-primary-100)",
                borderColor: "var(--color-primary-dark)",
                pointerEvents: 'none', // Ensure click-through to events
                zIndex: 0, // Explicit z-index
              }}
              title={`${a.start.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })} – ${a.end.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}`}
            />
          ))}

          {events.flatMap((e, idx) => {
            const eventStartDay = new Date(e.start);
            eventStartDay.setHours(0, 0, 0, 0);

            const eventEndDay = new Date(e.end);
            eventEndDay.setHours(0, 0, 0, 0);

            const currentDay = new Date(dayDate);
            currentDay.setHours(0, 0, 0, 0);

            const parts = [];

            if (currentDay < eventStartDay || currentDay > eventEndDay) {
              return parts;
            }

            let displayStart, displayEnd;
            let isSplit = false;
            let part = 0;

            const isFirstDay = sameDay(e.start, dayDate);
            const isLastDay = sameDay(e.end, dayDate);
            const isMiddleDay = !isFirstDay && !isLastDay;

            if (isFirstDay && isLastDay) {
              displayStart = e.start;
              displayEnd = e.end;
              isSplit = false;
            } else if (isFirstDay) {
              displayStart = e.start;
              displayEnd = new Date(e.start);
              displayEnd.setHours(24, 0, 0, 0);
              isSplit = true;
              part = 1;
            } else if (isLastDay) {
              displayStart = new Date(e.end);
              displayStart.setHours(0, 0, 0, 0);
              displayEnd = e.end;
              isSplit = true;
              part = 3;
            } else if (isMiddleDay) {
              displayStart = new Date(currentDay);
              displayStart.setHours(0, 0, 0, 0);
              displayEnd = new Date(currentDay);
              displayEnd.setHours(24, 0, 0, 0);
              isSplit = true;
              part = 2;
            }

            if (displayStart && displayEnd) {
              parts.push({ ...e, displayStart, displayEnd, isSplit, part });
            }

            return parts.map((part, pIdx) => {
              const estadoKey = (part.meta?.estadoKey || "").toUpperCase();
              const tripStatus = (part.meta?.viajeStatus || "").toUpperCase();

              const isPendingApproval = estadoKey === "PENDING";
              const isCompleted = tripStatus === "COMPLETADO";
              const label = part.meta?.nombre || part.title || "Reserva";

              const driverName =
                part.meta?.chofer ||
                part.meta?.nombreChofer ||
                part.meta?.NombreChofer ||
                part.meta?.driverName ||
                "";

              const timeStart = part.displayStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              const timeEnd = part.displayEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              const timeRange = `${timeStart} - ${timeEnd}`;

              let continuationText = "";
              if (part.isSplit) {
                if (part.part === 1) {
                  continuationText = "→";
                } else if (part.part === 2) {
                  continuationText = "↔";
                } else if (part.part === 3) {
                  continuationText = "←";
                }
              }

              const tooltipText = `${label}\n${timeRange}\nChofer: ${driverName || 'N/A'}${part.isSplit ? `\n(${part.part === 1 ? 'Continúa al día siguiente →' : part.part === 3 ? '← Viene del día anterior' : 'Día intermedio'})` : ''}`;

              // Calculate duration in minutes for this specific part
              const durationMinutes = (part.displayEnd - part.displayStart) / (1000 * 60);
              const isShort = durationMinutes <= 45;

              return (
                <button
                  key={`ev-${idx}-${pIdx}`}
                  className={
                    "wc-block wc-event" +
                    (isPendingApproval ? " pending" : "") +
                    (isCompleted ? " completed" : "")
                  }
                  style={{
                    ...blockStyle(part.displayStart, part.displayEnd),
                    backgroundImage: part.meta?.calendarGradient || getEventGradient(part.meta),
                    border: "1px solid rgba(15,23,42,.1)",
                    color: "#0f172a",
                    minHeight: "26px", // Ensure minimum visibility
                    zIndex: 1, // Base z-index
                  }}
                  title={tooltipText}
                  onClick={() => handleEventClick(part)}
                  onMouseEnter={(e) => { e.currentTarget.style.zIndex = 100; }}
                  onMouseLeave={(e) => { e.currentTarget.style.zIndex = 1; }}
                >
                  <span className="dot" />
                  <div
                    className="wc-event-content"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      width: "100%",
                      overflow: "hidden",
                      gap: "0px", // Reduced gap
                    }}
                  >
                    {continuationText && (
                      <span
                        style={{
                          fontSize: "1.2rem",
                          fontWeight: "700",
                          textAlign: "center",
                          width: "100%",
                          lineHeight: "1",
                        }}
                      >
                        {continuationText}
                      </span>
                    )}

                    {window.innerWidth <= 768 ? (
                      <span
                        style={{
                          fontSize: driverName ? "0.7rem" : "0.8rem",
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          width: "100%",
                          textAlign: "center",
                          lineHeight: "1.2"
                        }}
                        title={driverName}
                      >
                        {driverName ? getInitials(driverName) : getInitials(label)}
                      </span>
                    ) : (
                      // Desktop View: Toggle between Driver Name and Title (Motivo) based on showSchedule
                      <>
                        {isShort ? (
                          // Compact view for short events (<= 45 mins)
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            width: '100%',
                            height: '100%',
                            gap: '4px',
                            paddingLeft: '2px'
                          }}>
                            <span style={{
                              display: "block",
                              width: "100%",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              fontSize: "0.7rem",
                              fontWeight: "600",
                              lineHeight: "1.2"
                            }}>
                              {showSchedule ? label : (driverName || label)}
                            </span>
                          </div>
                        ) : (
                          // Standard view for longer events > 45min
                          <span
                            className="txt"
                            style={{
                              display: "block",
                              width: "100%",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              fontSize: "0.8rem",
                              fontWeight: "600"
                            }}
                          >
                            {showSchedule ? label : (driverName || label)}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </button>
              );
            });
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="wc-wrap">

      <div className="wc-top">
        <h3>{weekLabel}</h3>
        <div className="wc-nav">
          <button onClick={() => goto(-7)}>‹</button>
          <button onClick={() => onNavigate(new Date())}>Hoy</button>
          <button onClick={() => goto(7)}>›</button>
        </div>
      </div>

      <div className="wc-head">
        <div className="wc-gutter" />
        {days.map((d, i) => (
          <div key={i} className="wc-dayname" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: '1.2' }}>
            <span style={{ fontSize: '0.85em', textTransform: 'uppercase', opacity: 0.8 }}>{daysES[i]}</span>
            <span style={{ fontSize: '1.2em', fontWeight: 'bold' }}>{d.getDate()}</span>
          </div>
        ))}
      </div>

      <div className="wc-grid">
        <div className="wc-gutter">
          {hours.map((h, i) => (
            <div key={i} className="wc-hour">
              {h}
            </div>
          ))}
        </div>
        {days.map((d, i) => (
          <DayColumn key={i} dayDate={d} />
        ))}
      </div>
    </div>
  );
}